/**
 * Remplissage systeme Android (service d'autofill de l'app, `apps/android` KlavoxAutofillService).
 *
 * L'app native recoit la demande de l'OS et ouvre ce coffre avec une CIBLE qu'elle a fixee elle-meme --
 * `androidapp://<paquet>` pour une app, `https://<domaine>/` pour un navigateur reconnu -- jamais la page.
 * Le coffre ne remet au pont que l'identifiant et le mot de passe d'une entree qui RECONNAIT cette cible,
 * par le meme chemin que partout ailleurs (`entryMatchesSite` : regle par adresse, defaut du coffre,
 * domaines equivalents). Jamais de remplissage sur un site qui ne correspond pas.
 */
import { entryMatchesSite, uriMatchRuleOf, type SiteMatchSecrets } from '@klavox/vault-model';

/** Volet autofill du pont `KlavoxAndroid` (absent hors app Android, ou hors demande de remplissage). */
export interface AutofillBridge {
  /** Cible fixee par le service, `""` hors remplissage. */
  fillTarget?(): string;
  /** Rend l'identifiant choisi a l'OS ; un seul appel par demande. */
  fillWith?(username: string, password: string): void;
}

const APP_SCHEME = 'androidapp://';

/** Cible du remplissage en cours, `null` s'il n'y en a pas (ou si le pont rend autre chose qu'une cible connue). */
export function autofillTarget(bridge: AutofillBridge | undefined): string | null {
  const target = bridge?.fillTarget?.() ?? '';
  return target.startsWith(APP_SCHEME) || target.startsWith('https://') ? target : null;
}

/**
 * L'entree reconnait-elle la cible ? Une app ne se reconnait qu'a son paquet exact (`androidapp://`, forme
 * Bitwarden), sauf adresse reglee sur « jamais » ; un site, selon les regles de correspondance du coffre.
 * ponytail: pas de Digital Asset Links -- une app n'herite pas des entrees de son site ; resoudre
 * `assetlinks.json` cote natif (empreinte du certificat) pour lier app et domaine.
 */
export function entryFillsTarget(secrets: SiteMatchSecrets, target: string, defaultRule?: unknown): boolean {
  if (target.startsWith(APP_SCHEME)) {
    return (secrets.uris ?? []).some(
      (uri) => typeof uri === 'string' && uri.trim().toLowerCase() === target && uriMatchRuleOf(secrets, uri) !== 'never',
    );
  }
  return entryMatchesSite(secrets, target, defaultRule);
}

/** Remet l'identifiant de l'entree au pont, si et seulement si elle reconnait la cible. */
export function fillFromEntry(
  bridge: AutofillBridge,
  secrets: SiteMatchSecrets & { readonly username?: unknown; readonly password?: unknown },
  defaultRule?: unknown,
): boolean {
  const target = autofillTarget(bridge);
  if (target === null || bridge.fillWith === undefined || !entryFillsTarget(secrets, target, defaultRule)) return false;
  bridge.fillWith(
    typeof secrets.username === 'string' ? secrets.username : '',
    typeof secrets.password === 'string' ? secrets.password : '',
  );
  return true;
}

/** Libelle de la cible pour l'utilisateur : le domaine, ou le nom de paquet de l'app. */
export function autofillTargetLabel(target: string): string {
  return target.startsWith(APP_SCHEME) ? target.slice(APP_SCHEME.length) : new URL(target).hostname;
}
